import { onBeforeUnmount, ref, watch, type Ref } from 'vue'
import type { CompanyMarketData } from '@/types/company'

export interface MarketDataResult extends Partial<CompanyMarketData> {
  notFound?: boolean
}

interface UseCompanyMarketDataOptions {
  fetcher: (symbol: string) => Promise<MarketDataResult | null | undefined>
  onResult?: (result: MarketDataResult, meta: { fromUser: boolean }) => void
  debounceMs?: number
  minTickerLength?: number
  formatMarketCap?: (value: string | number | null | undefined) => string
}

const DEFAULT_DEBOUNCE = 600
const DEFAULT_MIN_LENGTH = 2

export const formatMarketCap = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return ''

  if (typeof value === 'string' && value.trim().toUpperCase() === 'SUSPENDED') {
    return 'SUSPENDED'
  }

  const num = typeof value === 'string' ? parseFloat(value) : value
  if (Number.isNaN(num)) return ''

  if (num >= 1_000_000_000) return `$${(num / 1_000_000_000).toFixed(2)}B AUD`
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M AUD`
  if (num >= 1_000) return `$${(num / 1_000).toFixed(2)}K AUD`
  return `$${num.toFixed(2)} AUD`
}

export function useCompanyMarketData(
  ticker: Ref<string | null | undefined>,
  marketCap: Ref<string | null | undefined>,
  options: UseCompanyMarketDataOptions
) {
  const error = ref<string | null>(null)
  const isFetching = ref(false)
  const formatFn = options.formatMarketCap ?? formatMarketCap
  const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE
  const minTickerLength = options.minTickerLength ?? DEFAULT_MIN_LENGTH

  let debounceTimer: number | null = null
  let fetchToken = 0
  let manualTickerEdit = false

  const clearPendingFetch = () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
    fetchToken += 1
    isFetching.value = false
  }

  const onTickerInput = (event: Event) => {
    const value = (event.target as HTMLInputElement).value
    error.value = null
    manualTickerEdit = true
    if (value.trim().length > 0) {
      marketCap.value = ''
    } else {
      marketCap.value = ''
      clearPendingFetch()
    }
  }

  const onMarketCapInput = (event: Event) => {
    const value = (event.target as HTMLInputElement).value
    error.value = null
    if (value.trim().length > 0) {
      ticker.value = ''
      clearPendingFetch()
    }
  }

  watch(
    ticker,
    (newTicker) => {
      error.value = null

      if (debounceTimer) {
        clearTimeout(debounceTimer)
        debounceTimer = null
      }

      if (manualTickerEdit && newTicker && newTicker.trim().length > 0) {
        marketCap.value = ''
      }

      const wasManual = manualTickerEdit
      manualTickerEdit = false

      if (!newTicker || newTicker.trim().length < minTickerLength) {
        clearPendingFetch()
        return
      }

      isFetching.value = true
      const currentToken = ++fetchToken
      const symbol = newTicker.trim().toUpperCase()

      debounceTimer = window.setTimeout(async () => {
        debounceTimer = null

        try {
          const result = await options.fetcher(symbol)

          if (currentToken !== fetchToken) {
            return
          }

          if (result?.notFound) {
            error.value = 'Symbol not found. Please check your ASX ticker.'
            ticker.value = ''
            marketCap.value = ''
            return
          }

          if (result) {
            marketCap.value = formatFn(result.market_cap ?? null)
            options.onResult?.(result, { fromUser: wasManual })
            error.value = null
          } else {
            error.value = 'Unable to fetch market data. Please try again.'
          }
        } catch (err) {
          console.error('Failed to fetch market data', err)
          if (currentToken === fetchToken) {
            error.value = 'Unable to fetch market data. Please try again.'
          }
        } finally {
          if (currentToken === fetchToken) {
            isFetching.value = false
          }
        }
      }, debounceMs)
    },
    { flush: 'post' }
  )

  onBeforeUnmount(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }
  })

  return {
    isFetching,
    error,
    onTickerInput,
    onMarketCapInput,
  }
}
